import React from 'react'
import { SimpleLayout } from '../../layouts/simple'
import { Teacher } from '../../providers/context'
import { useNavigate } from 'react-router-dom'
import { SpecialPanel } from '../../layouts/components/panel'
import { TextInput } from '../../components/form/textInput'
import { Loader } from '../../components/simple/loader'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../request'
import { Button, ButtonStyle } from '../../components/simple/button'
import { Popups } from '../../providers/popups'
import { ConferenceInterpretersModal, ConferenceTimeEditor } from './newConference'
import { ConferenceTimeRange } from './components/conferenceComponents'
import { pluralize } from '../../utils'

type ConferenceEditData = {
  name: string
  id: number
  times: ConferenceTimeRange[]
  interpreters?: Record<string, number>
  adminLed: boolean
}

export const PTCEditConferencePage: React.FC<{ user: Teacher; id: number }> = ({ user, id }) => {
  const { data: conference } = useRequest<ConferenceEditData>(
    `/frontend_api/getConferenceForEdit`,
    React.useMemo(() => ({ id }), [id])
  )
  return (
    <SimpleLayout>
      {conference ? <PTCEditConference user={user} conference={conference} /> : <Loader className="mt-8" />}
    </SimpleLayout>
  )
}

const PTCEditConference: React.FC<{ user: Teacher; conference: ConferenceEditData }> = ({ user, conference }) => {
  const [loading, setLoading] = React.useState(false)
  const navigate = useNavigate()

  const [editingInterpreters, setEditingInterpreters] = React.useState(false)

  const [selectedInterpreters, setSelectedInterpreters] = React.useState<Record<string, number>>(
    conference.interpreters || {}
  )
  const interpreterCount = Object.values(selectedInterpreters).reduce((a, b) => a + b, 0)

  const [name, setName] = React.useState(conference.name)
  const [conferenceTimes, setConferenceTimes] = React.useState<ConferenceTimeRange[]>(conference.times)
  return (
    <SpecialPanel className={`mb-8 mt-8 sm:!w-auto ${loading ? 'pointer-events-none opacity-50' : ''}`}>
      <div className="text-2xl font-bold">Edit Conference</div>
      {conference.adminLed && (
        <Button onClick={() => navigate(`/ptc/adminConferenceVerification/exclusionClasses/${conference.id}`)}>
          Edit Exclusion Classes
        </Button>
      )}
      <div>Conference Name</div>
      <TextInput className="w-full" blue value={name} onChange={setName} />
      {conference.interpreters && (
        <>
          <Button buttonStyle={ButtonStyle.blue} onClick={() => setEditingInterpreters(true)}>
            {interpreterCount} {pluralize(interpreterCount, 'interpreter')} <b>(Edit)</b>
          </Button>
          <ConferenceInterpretersModal
            open={editingInterpreters}
            onClose={() => setEditingInterpreters(false)}
            interpreters={selectedInterpreters}
            setInterpreters={setSelectedInterpreters}
          />
        </>
      )}

      <ConferenceTimeEditor ranges={conferenceTimes} setRanges={setConferenceTimes} />

      <div className="mt-4 flex justify-between gap-2 self-stretch">
        <Button
          logo
          onClick={() => {
            setLoading(true)
            makeRequest('/frontend_api/editConference', {
              id: conference.id,
              conferenceDate: conferenceTimes.map((r) => r.date),
              startTime: conferenceTimes.map((r) => r.startTime),
              endTime: conferenceTimes.map((r) => r.endTime),
              interpretersData: conference.interpreters
                ? JSON.stringify(
                    Object.entries(selectedInterpreters).map(([k, v]) => ({ language: k, availability: v }))
                  )
                : undefined,
              conferenceName: name,
            })
              .then(() => navigate(`/ptc/conference/${conference.id}`))
              .catch(showAxiosErrorAsToast)
              .finally(() => setLoading(false))
          }}
          disabled={!name || conferenceTimes.length === 0}
        >
          Save
        </Button>
        <Button
          buttonStyle={ButtonStyle.warning}
          onClick={async () => {
            if (await Popups.simpleConfirm('Are you sure you want to delete this conference?')) {
              makeRequest<boolean>('/ptc/conference/delete/' + conference.id)
                .then((response) => {
                  if (response) navigate('/')
                  else Popups.simplePopup('Conference could not be deleted')
                })
                .catch(showAxiosErrorAsToast)
            }
          }}
        >
          Delete Conference
        </Button>
      </div>
    </SpecialPanel>
  )
}
